import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CountingDocument, InventoryList, InventoryListItem, SearchCriteria } from '@raptor-fe/core';
import { ServiceUtils } from '../service-utils/service-utils';
import { LoadingHeaders } from '../web-loading-service/web-loading-headers';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class InventoryListService {
    private readonly http = inject(HttpClient);
    private readonly serviceUtils = inject(ServiceUtils);
    private httpHeadersWithNoLoading = LoadingHeaders.httpHeadersWithNoLoading();

    search(tenantId: string, branchId: string, criteria: SearchCriteria): Observable<InventoryList[]> {
        return this.http.post<InventoryList[]>(this.serviceUtils.getBaseUrl('inventoryList', tenantId, branchId) + `/search`, criteria);
    }

    searchByKeyword(
        tenantId: string,
        branchId: string,
        keyword: string,
        status: 'active' | 'completed' | 'pendingApproval' | 'all',
        criteria: SearchCriteria,
        showLoading = true,
    ): Observable<InventoryList[]> {
        if (status === 'pendingApproval') {
            criteria.items.push({
                condition: 'and',
                name: 'countingDocument.submittedDate',
                operator: '<>',
                value: null,
            });
            criteria.items.push({
                condition: 'and',
                name: 'countingDocument.approvalDate',
                operator: '=',
                value: null,
            });
        } else if (status === 'completed') {
            criteria.items.push({
                condition: 'and',
                name: 'countingDocument.approvalDate',
                operator: '<>',
                value: null,
            });
        } else if (status === 'active') {
            criteria.items.push({
                condition: 'and',
                name: 'countingDocument.approvalDate',
                operator: '=',
                value: null,
            });
        }

        return this.http.post<InventoryList[]>(
            this.serviceUtils.getBaseUrl('inventoryList', tenantId, branchId) + `/search/keyword/${keyword}`,
            criteria,
            {
                headers: showLoading ? undefined : this.httpHeadersWithNoLoading,
            },
        );
    }

    get(tenantId: string, branchId: string, id: string): Observable<InventoryList> {
        return this.http.get<InventoryList>(this.serviceUtils.getBaseUrl('inventoryList', tenantId, branchId) + `/${id}`);
    }

    approve(tenantId: string, branchId: string, id: string): Observable<InventoryList> {
        return this.http.put<InventoryList>(this.serviceUtils.getBaseUrl('inventoryList', tenantId, branchId) + `/approve/${id}`, {});
    }

    assignCountingDocument(
        tenantId: string,
        branchId: string,
        inventoryListId: string,
        countingDocument: CountingDocument,
    ): Observable<InventoryList> {
        return this.http.put<InventoryList>(
            this.serviceUtils.getBaseUrl('inventoryList', tenantId, branchId) + `/assign-counting-document/${inventoryListId}`,
            countingDocument,
        );
    }

    save(tenantId: string, branchId: string, inventoryList: InventoryList): Observable<InventoryList> {
        if (inventoryList.id) {
            return this.http.put<InventoryList>(this.serviceUtils.getBaseUrl('inventoryList', tenantId, branchId), inventoryList);
        } else {
            return this.http.post<InventoryList>(this.serviceUtils.getBaseUrl('inventoryList', tenantId, branchId), inventoryList);
        }
    }

    delete(tenantId: string, branchId: string, id: string): Observable<void> {
        return this.http.delete<void>(this.serviceUtils.getBaseUrl('inventoryList', tenantId, branchId) + `/${id}`);
    }

    /**
     * Adds an off-list part (resolved to an inventory) to an existing inventory list.
     * The inventory id is supplied as a route parameter and the request body is empty.
     * Returns the fully populated inventory list including the new item, its inventory,
     * part, barcodes, and counting-document items.
     */
    addItem(tenantId: string, branchId: string, id: string, inventoryId: string): Observable<InventoryList> {
        return this.http.post<InventoryList>(this.serviceUtils.getBaseUrl('inventoryList', tenantId, branchId) + `/${id}/item/${inventoryId}`, {});
    }

    updateItemScannedQuantity(tenantId: string, branchId: string, id: string, scannedQuantity: number): Observable<InventoryListItem> {
        return this.http.put<InventoryListItem>(
            this.serviceUtils.getBaseUrl('inventoryList', tenantId, branchId) + `/update-item-scanned-quantity/${id}`,
            scannedQuantity,
        );
    }
}
